<template>
  <div class="admin-inline-tools">
    <div class="admin-inline-tools__search-wrap">
      <label class="admin-inline-tools__search" aria-label="搜索">
        <span class="admin-inline-tools__search-tag">搜索</span>
        <input
          v-model="searchQuery"
          type="text"
          placeholder="搜索班级 / 设备 / 功能"
          @focus="handleSearchFocus"
          @input="handleSearchInput"
          @keydown.enter.prevent="submitSearch"
          @keydown.esc.prevent="closeSearch"
          @blur="scheduleCloseSearch"
        >
      </label>
      <div v-if="showSearchDropdown && searchQuery.trim()" class="admin-inline-tools__search-dropdown">
        <div v-if="visibleSearchResults.length" class="admin-inline-tools__search-results">
          <button
            v-for="item in visibleSearchResults"
            :key="searchResultKey(item)"
            type="button"
            class="admin-inline-tools__search-result"
            @mousedown.prevent="applySearchResult(item)"
          >
            <span class="admin-inline-tools__search-result-title">{{ item.label }}</span>
            <span class="admin-inline-tools__search-result-meta">{{ item.meta }}</span>
          </button>
        </div>
        <div v-else class="admin-inline-tools__search-empty">
          {{ searchDataLoading ? '搜索中...' : '未找到匹配结果' }}
        </div>
      </div>
    </div>
    <div class="admin-inline-tools__actions">
      <NotificationCenter />
      <button type="button" class="admin-inline-tools__logout" @click="handleLogout">退出登录</button>
    </div>
  </div>
</template>

<script>
import NotificationCenter from './NotificationCenter.vue'

const ADMIN_FUNCTIONS = [
  ['管理首页', '/', '功能 · 工作台'],
  ['功能导航', '/nav', '功能 · 工作台'],
  ['班牌设备管理', '/admin/boards', '功能 · 设备管理'],
  ['设备统一配置', '/admin/boards/config', '功能 · 设备管理'],
  ['班级管理', '/admin/classes', '功能 · 班级管理'],
  ['考勤管理', '/attendance', '功能 · 班级管理'],
  ['签到记录', '/attendance/records', '功能 · 班级管理'],
  ['出入登记管理', '/admin/class-access/records', '功能 · 班级管理'],
  ['班级荣誉管理', '/admin/class-honors', '功能 · 班级管理'],
  ['学生管理', '/admin/students', '功能 · 用户管理'],
  ['学生人脸管理', '/admin/students/face-manage', '功能 · 用户管理'],
  ['教师管理', '/admin/teachers', '功能 · 用户管理'],
  ['校医管理', '/admin/school-doctors', '功能 · 用户管理'],
  ['心理老师管理', '/admin/psychology-counselors', '功能 · 用户管理'],
  ['管理员中心', '/admin/center', '功能 · 用户管理'],
  ['请假审批', '/leave', '功能 · 请假管理'],
  ['取物管理', '/pickup', '功能 · 请假管理'],
  ['德育记录', '/moral/records', '功能 · 德育管理'],
  ['德育评价', '/moral/score', '功能 · 德育管理'],
  ['家校沟通', '/home-school', '功能 · 家校互通'],
  ['聊天敏感词', '/config/home-school-sensitive', '功能 · 家校互通'],
  ['课表管理', '/schedule', '功能 · 教学管理'],
  ['作业管理', '/homework', '功能 · 教学管理'],
  ['留校生考勤', '/admin/stay-school/attendance', '功能 · 教学管理'],
  ['AI问答管理', '/ai/chat-sessions', '功能 · 教学管理'],
  ['菜品管理', '/admin/meal/dishes', '功能 · 点餐管理'],
  ['菜单管理', '/admin/meal/menus', '功能 · 点餐管理'],
  ['订单管理', '/admin/meal/orders', '功能 · 点餐管理'],
  ['学校通知', '/notices', '功能 · 通知与内容'],
  ['学校新闻', '/news', '功能 · 通知与内容'],
  ['宿舍管理', '/admin/dorm/buildings', '功能 · 宿舍管理'],
  ['查寝管理', '/admin/dorm/inspection', '功能 · 宿舍管理'],
  ['寝室打卡', '/admin/dorm/sign-in', '功能 · 宿舍管理'],
  ['数据导入中心', '/import/students', '功能 · 数据与配置'],
  ['通校生出校门时间配置', '/admin/students/commuters', '功能 · 数据与配置'],
  ['德育评价项目', '/config/moral-items', '功能 · 数据与配置'],
  ['考勤时间配置', '/config/attendance-time', '功能 · 数据与配置'],
  ['学期管理', '/config/semesters', '功能 · 数据与配置'],
  ['作息方案管理', '/config/time-plans', '功能 · 数据与配置'],
  ['作息时间表', '/config/schedule-time', '功能 · 数据与配置'],
  ['一卡通卡号同步', '/config/campus-card-sync', '功能 · 数据与配置'],
  ['APIkey管理', '/config/api-keys', '功能 · 数据与配置']
]

function normalizeSearchText(...values) {
  return values
    .flat()
    .map(value => String(value || '').trim().toLowerCase())
    .filter(Boolean)
    .join(' ')
}

function createSearchItem({ label, meta, path, query, keywords = [] }) {
  return {
    label,
    meta,
    path,
    query,
    searchText: normalizeSearchText(label, meta, keywords)
  }
}

export default {
  name: 'AdminInlineTools',
  components: { NotificationCenter },
  data() {
    return {
      searchQuery: '',
      showSearchDropdown: false,
      searchDataLoading: false,
      closeSearchTimer: null
    }
  },
  computed: {
    classList() {
      return this.$store.state.classManage?.list ?? []
    },
    deviceList() {
      return this.$store.state.devices?.list ?? []
    },
    functionSearchItems() {
      return ADMIN_FUNCTIONS.map(([label, path, meta]) => createSearchItem({ label, path, meta }))
    },
    entitySearchItems() {
      const classItems = this.classList.map(cls => createSearchItem({
        label: cls.name || `班级 ${cls.id}`,
        meta: `班级 · ${cls.grade || '未设置年级'}`,
        path: '/admin/classes',
        query: { keyword: cls.name || cls.id || '' },
        keywords: [cls.id, cls.teacher, cls.machineCode, cls.boardId]
      }))
      const deviceItems = this.deviceList.map(device => {
        const boundClass = this.classList.find(cls => String(cls.deviceId || cls.boardId || '') === String(device.deviceId || device.id || ''))
        return createSearchItem({
          label: device.deviceId || device.id || '未设置设备编号',
          meta: `设备 · ${device.machineCode || '无机器码'} · ${boundClass?.name || '未绑定'}`,
          path: '/admin/boards',
          query: { keyword: device.machineCode || device.deviceId || device.id || '' },
          keywords: [boundClass?.name, device.status]
        })
      })
      return [...classItems, ...deviceItems]
    },
    visibleSearchResults() {
      const query = this.searchQuery.trim().toLowerCase()
      if (!query) return []
      return [...this.functionSearchItems, ...this.entitySearchItems]
        .filter(item => item.searchText.includes(query))
        .slice(0, 8)
    }
  },
  beforeUnmount() {
    if (this.closeSearchTimer) window.clearTimeout(this.closeSearchTimer)
  },
  methods: {
    searchResultKey(item) {
      return `${item.path}::${item.label}::${item.query?.keyword || ''}`
    },
    async ensureSearchData() {
      if (this.searchDataLoading) return
      this.searchDataLoading = true
      try {
        await Promise.all([
          this.classList.length ? Promise.resolve() : this.$store.dispatch('classManage/fetchList', { page_size: 200 }).catch(() => {}),
          this.deviceList.length ? Promise.resolve() : this.$store.dispatch('devices/fetchList').catch(() => {})
        ])
      } finally {
        this.searchDataLoading = false
      }
    },
    handleSearchFocus() {
      if (this.closeSearchTimer) window.clearTimeout(this.closeSearchTimer)
      this.showSearchDropdown = true
      this.ensureSearchData().catch(() => {})
    },
    handleSearchInput() {
      this.showSearchDropdown = true
      if (this.searchQuery.trim()) {
        this.ensureSearchData().catch(() => {})
      }
    },
    scheduleCloseSearch() {
      if (this.closeSearchTimer) window.clearTimeout(this.closeSearchTimer)
      this.closeSearchTimer = window.setTimeout(() => {
        this.showSearchDropdown = false
      }, 120)
    },
    closeSearch() {
      if (this.closeSearchTimer) window.clearTimeout(this.closeSearchTimer)
      this.showSearchDropdown = false
    },
    submitSearch() {
      if (!this.visibleSearchResults.length) return
      this.applySearchResult(this.visibleSearchResults[0])
    },
    applySearchResult(item) {
      this.closeSearch()
      this.searchQuery = ''
      this.$router.push({
        path: item.path,
        query: item.query || undefined
      }).catch(() => {})
    },
    async handleLogout() {
      if (await this.$uiConfirm('确定要退出登录吗？')) {
        this.$store.dispatch('logout')
        this.$router.push('/auth/login')
      }
    }
  }
}
</script>

<style scoped>
.admin-inline-tools {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  flex-wrap: nowrap;
}

.admin-inline-tools__search-wrap {
  position: relative;
  flex: 1 1 auto;
  min-width: 0;
  max-width: none;
}

.admin-inline-tools__search {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  min-height: 44px;
  padding: 0 14px;
  border: 1px solid rgba(15, 26, 34, 0.08);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.86);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.7);
}

.admin-inline-tools__search:focus-within {
  border-color: rgba(21, 122, 110, 0.34);
  box-shadow: 0 0 0 4px rgba(21, 122, 110, 0.08);
}

.admin-inline-tools__search-tag {
  font-size: 12px;
  font-weight: 700;
  color: var(--muted);
  white-space: nowrap;
}

.admin-inline-tools__search input {
  flex: 1;
  min-width: 0;
  height: 42px;
  padding: 0;
  border: none;
  outline: none;
  background: transparent;
  font-size: 13px;
  color: var(--ink);
}

.admin-inline-tools__search-dropdown {
  position: absolute;
  top: calc(100% + 10px);
  left: 0;
  right: 0;
  min-width: 320px;
  padding: 8px;
  border-radius: 16px;
  background: rgba(255, 252, 247, 0.98);
  border: 1px solid rgba(15, 26, 34, 0.08);
  box-shadow: 0 18px 36px rgba(15, 26, 34, 0.14);
  z-index: 30;
}

.admin-inline-tools__search-results {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.admin-inline-tools__search-result {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 10px 12px;
  border: none;
  border-radius: 12px;
  background: transparent;
  cursor: pointer;
  text-align: left;
}

.admin-inline-tools__search-result:hover {
  background: rgba(21, 122, 110, 0.08);
}

.admin-inline-tools__search-result-title {
  font-size: 13px;
  font-weight: 700;
  color: #20323a;
}

.admin-inline-tools__search-result-meta {
  font-size: 11px;
  color: var(--muted);
  text-align: right;
}

.admin-inline-tools__search-empty {
  padding: 14px 12px;
  font-size: 12px;
  color: var(--muted);
  text-align: center;
}

.admin-inline-tools__actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: nowrap;
  margin-left: auto;
  flex-shrink: 0;
}

.admin-inline-tools__actions :deep(.notification-bell) {
  width: 42px;
  height: 42px;
  border-radius: 999px;
}

.admin-inline-tools__logout {
  min-height: 42px;
  padding: 0 16px;
  border: none;
  border-radius: 999px;
  background: rgba(15, 26, 34, 0.06);
  color: var(--muted);
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.2s, color 0.2s;
}

.admin-inline-tools__logout:hover {
  background: rgba(196, 69, 69, 0.12);
  color: #c44545;
}

@media (max-width: 768px) {
  .admin-inline-tools {
    flex-wrap: wrap;
    gap: 10px;
  }

  .admin-inline-tools__search-wrap {
    flex-basis: 100%;
    max-width: none;
  }

  .admin-inline-tools__search-dropdown {
    min-width: 0;
  }

  .admin-inline-tools__actions {
    width: 100%;
    flex-wrap: wrap;
    justify-content: space-between;
    margin-left: 0;
  }
}

@media (max-width: 480px) {
  .admin-inline-tools__search {
    min-height: 40px;
    padding: 0 12px;
  }

  .admin-inline-tools__search input {
    height: 38px;
    font-size: 12px;
  }

  .admin-inline-tools__search-tag,
  .admin-inline-tools__logout {
    font-size: 12px;
  }
}
</style>
